function createObjectKeysArray<T extends object>(obj: T): (keyof T)[] {
  const keys = Object.keys(obj);
  return keys as (keyof T)[];
}

export const ATTRIBUTIONS_MAP = {
  1: "ETICISTA",
  2: "JURIDICO",
  3: "ETICO",
  4: "COMITE",
  5: "ADMIN",
  6: "ORGANIZACAO",
} as const;

export const ATTRIBUTIONS_VALUE_MAP = {
  1: "ETICI",
  2: "SOJUR",
  3: "ETICO",
  4: "COMIT",
  5: "ADMIN",
  6: "ORGAN",
} as const;

export const USER_ATTRIBUTIONS = {
  1: "Eticista",
  2: "Especialista Sócio-jurídico",
  3: "Especialista Ético",
  4: "Membro do Comitê de Avaliação",
  5: "Administrador",
  6: "Organização",
} as const;

const TEMP_ATTRIBUTIONS = Object.values(USER_ATTRIBUTIONS);

export type ATTRIBUTION = (typeof TEMP_ATTRIBUTIONS)[number];

export const ATTRIBUTION_NAMES = {
  ETICISTA: "Eticista",
  SOCIO_JURIDICO: "Especialista Sócio-jurídico",
  ETICO: "Especialista Ético",
  COMITE: "Membro do Comitê de Avaliação",
  ADMIN: "Administrador",
  ORGANIZACAO: "Organização",
} as const satisfies Record<string, ATTRIBUTION>;

export type ATTRIBUTION_NAME =
  (typeof ATTRIBUTIONS_MAP)[keyof typeof ATTRIBUTIONS_MAP];

// Perfis de acesso aceitos no cadastro de usuário RAIP (POST /users). O backend converte cada um na atribuição correspondente.
export const RAIP_ROLES = {
  ADMIN: "admin",
  ETICISTA: "eticista",
  SOCIO_JURIDICO: "socio_juridico",
  ETICO_LEGAL: "etico_legal",
  MEMBRO_COMITE: "membro_comite",
} as const;

export type RAIP_ROLE = (typeof RAIP_ROLES)[keyof typeof RAIP_ROLES];

export const RAIP_ROLE_VALUES = Object.values(RAIP_ROLES) as [
  RAIP_ROLE,
  ...RAIP_ROLE[],
];

export const RAIP_ROLE_OPTIONS: { value: RAIP_ROLE; label: ATTRIBUTION }[] = [
  { value: RAIP_ROLES.ADMIN, label: ATTRIBUTION_NAMES.ADMIN },
  { value: RAIP_ROLES.ETICISTA, label: ATTRIBUTION_NAMES.ETICISTA },
  { value: RAIP_ROLES.SOCIO_JURIDICO, label: ATTRIBUTION_NAMES.SOCIO_JURIDICO },
  { value: RAIP_ROLES.ETICO_LEGAL, label: ATTRIBUTION_NAMES.ETICO },
  { value: RAIP_ROLES.MEMBRO_COMITE, label: ATTRIBUTION_NAMES.COMITE },
];

export const ROLES = {
  ADMIN: "admin",
  PARTNER: "partner",
  RESP: "resp",
  MEMBER: "member",
} as const;

export const USER_STATUS = {
  ativ: "Ativo",
  inat: "Inativo",
  agat: "Aguardando ativação",
} as const;

export type USER_STATUS_KEY = keyof typeof USER_STATUS;

const USER_RAIP_ROLES = {
  partner: "Equipe",
  admin: "Administrador",
} as const;

export const RAIP_ROLES_KEYS = createObjectKeysArray(USER_RAIP_ROLES);

export type RaipRole = keyof typeof USER_RAIP_ROLES;

export const USER_ORGANIZATION_ROLES = {
  resp: "Responsável",
  member: "Membro",
} as const;

export type OrganizationRole = keyof typeof USER_RAIP_ROLES;

export const USER_ROLES = {
  ...USER_RAIP_ROLES,
  ...USER_ORGANIZATION_ROLES,
} as const;

export const USER_RESPONSIBLE_PERMISSIONS = [
  "Gerenciar aplicações (Criar, Editar, Excluir, Submeter e Cancelar submissão)",
  "Gerenciar usuários credenciados (Adicionar, Alterar, Alterar acessos)",
  "Gerenciar status de organização (Ativar e Desativar)",
] as const;

export const USER_QUICK_GUIDES = [
  {
    theme: "Autenticação",
    topics: [
      {
        id: "auth-access-management",
        title: "Gerenciamento de acesso",
      },
    ],
  },
  {
    theme: "Organização",
    topics: [
      {
        id: "organization-definition",
        title: "Definição",
      },
      {
        id: "organization-register",
        title: "Cadastro de organização",
      },
      {
        id: "organization-management",
        title: "Gerenciamento de organização",
        roles: [ATTRIBUTION_NAMES.ORGANIZACAO],
      },
    ],
  },
  {
    theme: "Princípios Éticos",
    topics: [
      { id: "principles-definition", title: "Definição" },
      {
        id: "principles-management",
        title: "Gerenciamento de princípios",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
        ],
      },
      {
        id: "principles-questions-vs-principles",
        title: "Perguntas x Princípios Éticos",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
        ],
      },
      {
        id: "principles-impact-calculation",
        title: "Cálculo de impacto",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
          ATTRIBUTION_NAMES.ETICISTA,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
    ],
  },
  {
    theme: "Perguntas",
    topics: [
      {
        id: "questions-definition",
        title: "Definição",
      },
      {
        id: "questions-management",
        title: "Gerenciamento de perguntas",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
        ],
      },
    ],
  },
  {
    theme: "Seções",
    topics: [
      {
        id: "sections-definition",
        title: "Definição",
      },
      {
        id: "sections-management",
        title: "Gerenciamento de seções",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
        ],
      },
    ],
  },
  {
    theme: "AIA",
    topics: [
      {
        id: "aia-definition",
        title: "Definição",
      },
      {
        id: "aia-register",
        title: "Cadastro de nova AIA",
        roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.SOCIO_JURIDICO],
      },
      {
        id: "aia-questionnaire-register",
        title: "Cadastro de questionário AIA",
        roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.SOCIO_JURIDICO],
      },
      {
        id: "aia-questionnaire-approval",
        title: "Homologação de questionário AIA",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
    ],
  },
  {
    theme: "EPS",
    topics: [
      {
        id: "eps-definition",
        title: "Definição",
      },
      {
        id: "eps-register",
        title: "Cadastro de nova EPS",
        roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.ETICO],
      },
      {
        id: "eps-questionnaire-register",
        title: "Cadastro de questionário EPS",
        roles: [ATTRIBUTION_NAMES.ADMIN, ATTRIBUTION_NAMES.ETICO],
      },
      {
        id: "eps-questionnaire-approval",
        title: "Homologação de questionário EPS",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.ETICO,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
    ],
  },
  {
    theme: "Aplicações",
    topics: [
      {
        id: "application-register",
        title: "Cadastro de aplicação",
        roles: [ATTRIBUTION_NAMES.ORGANIZACAO],
      },
      {
        id: "application-follow-up",
        title: "Acompanhamento de aplicações",
        roles: [ATTRIBUTION_NAMES.ORGANIZACAO],
      },
      {
        id: "application-submission",
        title: "Submissão de aplicação",
        roles: [ATTRIBUTION_NAMES.ORGANIZACAO],
      },
      {
        id: "application-evaluation",
        title: "Avaliação de aplicações",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.ETICISTA,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
      {
        id: "application-deliberation",
        title: "Deliberação de avaliações",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.ETICISTA,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
    ],
  },
  {
    theme: "Metodologia",
    topics: [
      {
        id: "application-flow",
        title: "Fluxo de aplicações",
      },
      {
        id: "questionnaires-approval",
        title: "Homologações de questionários",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
      {
        id: "evaluations-approvals",
        title: "Homologações de avaliações",
        roles: [
          ATTRIBUTION_NAMES.ADMIN,
          ATTRIBUTION_NAMES.SOCIO_JURIDICO,
          ATTRIBUTION_NAMES.ETICO,
          ATTRIBUTION_NAMES.ETICISTA,
          ATTRIBUTION_NAMES.COMITE,
        ],
      },
    ],
  },
] as const;

type ExtractTopicIds<
  T extends readonly { topics: readonly { id: string }[] }[],
> = T[number]["topics"][number]["id"];

export type UserQuickGuideId = ExtractTopicIds<typeof USER_QUICK_GUIDES>;
